// @flow

import React, { useRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import * as selectors from "@modules/profile/selectors";
import actionCreators from "@modules/profile/actionCreator";
import type { MyAbsenceDraft } from "@modules/profile/reducer/my-absences/types";

import { Button, SchemaForm, type StepProps } from "@components";
import { uiSchemaFnc } from "./ui-schema-classifications";
import { accountAbsenceManagementSettings } from "@modules/account/selectors";
import type { ClientSettingsSettingsType } from "@modules/settings/types";

type PropsFromState = $Exact<{
  schema: Object | null,
  draft: MyAbsenceDraft | null,
  isValid: boolean,
  clientSettings: ClientSettingsSettingsType | null,
}>;

type PropsFromDispatch = $Exact<{
  updateDraft: typeof actionCreators.SetMyAbsenceDraft,
  createAbsence: typeof actionCreators.CreateMyAbsence,
  updateAbsence: typeof actionCreators.UpdateMyAbsence,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
  isEdit: boolean,
  absenceId?: number,
  clientId: number,
}>;

function MyClassifications(props: Props) {
  if (!props.editable) {
    return null;
  }

  const formRef = useRef(null);

  function data(): MyAbsenceDraft {
    const { draft } = props;

    // if client blocks their employees to be able to share their consent to share status
    // default value of consentToShare property should be TRUE
    let consentToShare = draft?.consentToShare !== null ? draft?.consentToShare : null;
    consentToShare = clientBlockConsentToShare() === true ? true : consentToShare;

    // predefined data
    return {
      ...draft,
      isWorkRelated: draft?.isWorkRelated || false, // by default should be false
      isAccidentRelated: draft?.isAccidentRelated || false, // by default should be false
      consentToShare,
    };
  }

  function clientBlockConsentToShare(): boolean {
    return props.clientSettings?.blockConsent || false;
  }

  function uiSchema(): Object {
    // if client blocks their employees to be able to share their consent to share status
    // consentToShare property should be HIDDEN
    return uiSchemaFnc(clientBlockConsentToShare() !== true);
  }

  function nextStep(): void {
    if (formRef) {
      const btn = formRef.current.submit();
    }
  }

  function handleChange(formData: Object): void {
    // if absence reason changed, then reset consentToShare value to null
    let resetConsentToShare =
      formData?.absenceReason?.id !== data()?.absenceReason?.id ? null : formData.consentToShare;

    let consentToShare =
      clientBlockConsentToShare() === true ? formData.consentToShare : resetConsentToShare;

    const updatedDraft = {
      ...data(),
      ...formData,
      consentToShare,
    };

    props.updateDraft(updatedDraft);
  }

  function handleSubmit(formData): void {
    if (formData) {
      const updatedDraft = { ...props.draft, ...formData };

      if (props.isEdit || props.absenceId) {
        props.updateAbsence({
          clientId: props.clientId,
          absenceId: props.absenceId,
          data: {
            ...updatedDraft,
            daysLost: updatedDraft.daysLost.map(el => ({
              // the BE provides the values of null when GET request returns the data
              // they have some issue in their logic while PUT the data, so we pass "0" (zero) instead
              // TODO revise with the BE team
              recordedDay: el.recordedDay === null ? 0 : el.recordedDay,
              timeLostInSeconds: el.timeLostInSeconds === null ? 0 : el.timeLostInSeconds,
              workingTimeInSeconds: el.workingTimeInSeconds === null ? 0 : el.workingTimeInSeconds,
            })),
          },
        });
      } else {
        props.createAbsence({ clientId: props.clientId, data: updatedDraft });
      }

      // submit the step - load next step
      if (props.handleSubmit) {
        props.handleSubmit(updatedDraft, true);
      }
    }
  }

  return (
    <>
      <div className="w-100 d-flex justify-content-center">
        <div className="col-sm-10">
          <SchemaForm
            ref={formRef}
            className="w-100"
            formData={data() || {}}
            schema={props.schema || {}}
            uiSchema={uiSchema()}
            onChange={handleChange}
            onSubmit={handleSubmit}
            externalSubmit
          ></SchemaForm>
        </div>
      </div>
      <div className="w-100 d-flex justify-content-end mt-5 mb-3">
        <Button materialIcon="fast_forward" onClick={nextStep} disabled={!props.isValid}>
          {props.t("NEXT")}
        </Button>
      </div>
    </>
  );
}

const mapStateToProps = (state): PropsFromState => {
  return {
    schema: selectors.myClassificationsSchema(state),
    draft: selectors.myAbsenceDraft(state),
    isValid: selectors.isClassificationsValid(state),
    clientSettings: accountAbsenceManagementSettings(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  updateDraft: actionCreators.SetMyAbsenceDraft,
  createAbsence: actionCreators.CreateMyAbsence,
  updateAbsence: actionCreators.UpdateMyAbsence,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(MyClassifications);
